// ----- Wave E.3 — Lifecycle operation constants -----
//
// Single source of truth for the four eCTD lifecycle operations and their
// visual treatment, shared by the compiler frontend surfaces that render a
// lifecycle value (DocumentBadge, DocumentList, SectionTree) and the
// LifecycleOperationEditor.

export type LifecycleOperation = 'new' | 'replace' | 'append' | 'delete';

export const LIFECYCLE_OPERATIONS: readonly LifecycleOperation[] = [
  'new',
  'replace',
  'append',
  'delete',
] as const;

export function isLifecycleOperation(v: unknown): v is LifecycleOperation {
  return typeof v === 'string' && (LIFECYCLE_OPERATIONS as readonly string[]).includes(v);
}

/**
 * Canonical NON-defaulting parser: return the known (case-insensitive)
 * operation, or `null` when the value is absent or unrecognised. This is the
 * ONE evaluator every surface uses — it never invents 'new', so a display can
 * render an honest "Unknown" instead of implying a creation. It does not trim,
 * matching the long-standing acceptance of `normalizeLifecycle` (which now
 * delegates here), so the two can never disagree on the same input.
 */
export function parseLifecycleOperation(v: string | null | undefined): LifecycleOperation | null {
  if (typeof v === 'string') {
    const lower = v.toLowerCase();
    if (isLifecycleOperation(lower)) return lower;
  }
  return null;
}

/**
 * Normalize a lifecycle value into a known `LifecycleOperation`, falling back
 * to 'new' when it is absent or unrecognised.
 *
 * The 'new' fallback is the genuine default for the mutation/edit paths: a
 * document with no recorded operation yet is treated as a new leaf. Use this
 * ONLY where that default is correct. For *display* — where inventing 'new'
 * would misreport an unknown value — call `parseLifecycleOperation` directly
 * and render "Unknown" for null. This defers to the canonical parser so the
 * recognised set and case handling stay identical.
 */
export function normalizeLifecycle(v: string | null | undefined): LifecycleOperation {
  return parseLifecycleOperation(v) ?? 'new';
}

export interface LifecycleVisual {
  readonly label: string;
  readonly background: string;
  readonly color: string;
  readonly border: string;
  /** When true, the doc title should render with a strike-through. */
  readonly strikethrough: boolean;
}

export const LIFECYCLE_VISUAL: Record<LifecycleOperation, LifecycleVisual> = {
  new: {
    label: 'NEW',
    background: 'var(--surface2, rgba(0,0,0,0.04))',
    color: 'var(--text-muted, #6b7280)',
    border: 'var(--border, rgba(0,0,0,0.10))',
    strikethrough: false,
  },
  replace: {
    label: 'REPLACE',
    background: 'rgba(59,130,246,0.15)',
    color: '#1d4ed8',
    border: 'rgba(59,130,246,0.35)',
    strikethrough: false,
  },
  append: {
    label: 'APPEND',
    background: 'rgba(34,197,94,0.15)',
    color: '#15803d',
    border: 'rgba(34,197,94,0.35)',
    strikethrough: false,
  },
  delete: {
    label: 'DELETE',
    background: 'rgba(239,68,68,0.15)',
    color: '#b91c1c',
    border: 'rgba(239,68,68,0.35)',
    strikethrough: true,
  },
};

/**
 * The honest "operation not known" badge treatment, used when a value is
 * absent or unrecognised (`parseLifecycleOperation` returned null). It reuses
 * the muted neutral palette but reads "Unknown" — never "NEW" — so the badge
 * does not imply a creation it cannot vouch for.
 */
export const UNKNOWN_LIFECYCLE_VISUAL: LifecycleVisual = {
  label: 'Unknown',
  background: 'var(--surface2, rgba(0,0,0,0.04))',
  color: 'var(--text-muted, #6b7280)',
  border: 'var(--border, rgba(0,0,0,0.10))',
  strikethrough: false,
};

/** Long-form descriptions for the lifecycle dropdown options. */
export const LIFECYCLE_DESCRIPTIONS: Record<LifecycleOperation, string> = {
  new: 'A brand-new document being added to this sequence.',
  replace: 'Replaces an existing document from a prior sequence.',
  append: 'Adds supplementary content to an existing document.',
  delete: 'Marks an existing document for deletion in this sequence.',
};
